// Tile geometry and loading for map image export: the pure math that turns a
// geographic view into a list of tile URLs and canvas positions, plus a
// promise-based tile loader. Uses Leaflet's own EPSG:3857 CRS, so the exported
// pixels line up with what the on-screen map draws.
import { CRS, Util, latLng } from 'leaflet';

export const TILE_SIZE = 256;
// Highest zoom with real tiles on the public base layers.
export const MAX_TILE_ZOOM = 19;
// Safety cap for very wide exports (1600x900 needs 8x5 tiles).
export const MAX_TILES = 160;

const crs = CRS.EPSG3857;

// Pixel bounds (world pixels at `zoom`) of a geographic bounds.
export function pixelBounds(bounds, zoom) {
  const nw = crs.latLngToPoint(latLng(bounds.north, bounds.west), zoom);
  const se = crs.latLngToPoint(latLng(bounds.south, bounds.east), zoom);
  return { minX: nw.x, minY: nw.y, maxX: se.x, maxY: se.y };
}

// Largest integer zoom whose pixel span still fits width x height (minus the
// inset). Degenerate bounds (a single point) end up at MAX_TILE_ZOOM.
export function fitZoom(bounds, width, height, inset = 0.12) {
  const innerWidth = width * (1 - inset);
  const innerHeight = height * (1 - inset);
  for (let zoom = MAX_TILE_ZOOM; zoom >= 0; zoom -= 1) {
    const box = pixelBounds(bounds, zoom);
    if (box.maxX - box.minX <= innerWidth && box.maxY - box.minY <= innerHeight) return zoom;
  }
  return 0;
}

// The exported view: zoom plus the world-pixel coordinate of the canvas
// top-left, centred on the bounds.
export function viewFor(bounds, width, height, inset = 0.12) {
  const zoom = fitZoom(bounds, width, height, inset);
  const center = crs.latLngToPoint(
    latLng((bounds.north + bounds.south) / 2, (bounds.west + bounds.east) / 2),
    zoom
  );
  return { zoom, originX: center.x - width / 2, originY: center.y - height / 2 };
}

// Canvas position of a lat/lon in the view.
export function projectPoint(view, lat, lon) {
  const point = crs.latLngToPoint(latLng(lat, lon), view.zoom);
  return { x: point.x - view.originX, y: point.y - view.originY };
}

// Tiles covering the canvas: URL plus the pixel offset to draw it at. Tiles
// outside the pole range are skipped; x wraps around the antimeridian.
export function tileJobs(source, view, width, height) {
  const count = Math.pow(2, view.zoom);
  const first = {
    x: Math.floor(view.originX / TILE_SIZE),
    y: Math.floor(view.originY / TILE_SIZE)
  };
  const cols = Math.ceil(width / TILE_SIZE) + 1;
  const rows = Math.ceil(height / TILE_SIZE) + 1;
  const jobs = [];
  for (let dy = 0; dy < rows && jobs.length < MAX_TILES; dy += 1) {
    for (let dx = 0; dx < cols && jobs.length < MAX_TILES; dx += 1) {
      const y = first.y + dy;
      if (y < 0 || y >= count) continue;
      const x = (((first.x + dx) % count) + count) % count;
      jobs.push({
        url: Util.template(source.url, { ...subdomain(source), z: view.zoom, x, y, r: '' }),
        left: (first.x + dx) * TILE_SIZE - view.originX,
        top: y * TILE_SIZE - view.originY
      });
    }
  }
  return jobs;
}

function subdomain(source) {
  const list = String(source.subdomains || 'abc');
  return { s: list[Math.floor(Math.random() * list.length)] || 'a' };
}

// Default tile loader: fetches a tile as a CORS image so the canvas stays
// untainted and can be serialized to PNG. Resolves null when it fails.
export function loadTileImage(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}
